import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select, Textarea } from '@artosera/ui';
import { useLieux } from '../../api/lieux.js';
import {
  aplatirEmplacements,
  reserveApi,
  useEmplacements,
  useMutationReserve,
  type EmplacementPresente,
  type NoeudEmplacement,
} from '../../api/reserve.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { Etiquettes } from '../inventaire/Etiquettes.js';
import styles from '../inventaire/InventairePage.module.css';

function FormulaireEmplacement({
  emplacement,
  tous,
  fermer,
}: {
  emplacement: EmplacementPresente | null;
  tous: EmplacementPresente[];
  fermer: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const lieux = useLieux(galerieSlug);
  const [nom, setNom] = useState(emplacement?.nom ?? '');
  const [code, setCode] = useState(emplacement?.code ?? '');
  const [lieuId, setLieuId] = useState(emplacement?.lieuId ?? '');
  const [parentId, setParentId] = useState(emplacement?.parentId ?? '');
  const [notes, setNotes] = useState(emplacement?.notes ?? '');
  const [actif, setActif] = useState(emplacement?.actif ?? true);
  const enregistrer = useMutationReserve(galerieSlug, (_: void, appel) => {
    const corps = { nom, code, lieuId, parentId: parentId || null, notes, actif };
    if (emplacement)
      return reserveApi.modifierEmplacement(galerieSlug, emplacement.id, corps, appel);
    return reserveApi.creerEmplacement(galerieSlug, corps, appel);
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) fermer();
      }}
      title={t(
        emplacement ? 'regie:reglages.emplacements.modifier' : 'regie:reglages.emplacements.creer',
      )}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.formulaire}>
        <Field htmlFor="emplacement-nom" label={t('regie:reglages.emplacements.nom')}>
          <Input
            id="emplacement-nom"
            maxLength={120}
            value={nom}
            onChange={(e) => setNom(e.target.value)}
          />
        </Field>
        <Field htmlFor="emplacement-code" label={t('regie:reglages.emplacements.code')}>
          <Input
            id="emplacement-code"
            maxLength={16}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </Field>
        <Field htmlFor="emplacement-lieu" label={t('regie:inventaire.lieu')}>
          <Select
            id="emplacement-lieu"
            value={lieuId || 'aucun'}
            onValueChange={(v) => {
              setLieuId(v === 'aucun' ? '' : v);
              setParentId('');
            }}
            options={[
              { value: 'aucun', label: t('regie:inventaire.choisir') },
              ...(lieux.data ?? [])
                .filter((l) => l.actif || l.id === lieuId)
                .map((l) => ({ value: l.id, label: l.nom })),
            ]}
          />
        </Field>
        <Field htmlFor="emplacement-parent" label={t('regie:reglages.emplacements.parent')}>
          <Select
            id="emplacement-parent"
            value={parentId || 'aucun'}
            onValueChange={(v) => setParentId(v === 'aucun' ? '' : v)}
            options={[
              { value: 'aucun', label: t('regie:reglages.emplacements.racine') },
              ...tous
                .filter((e) => e.lieuId === lieuId && e.id !== emplacement?.id)
                .map((e) => ({ value: e.id, label: e.chemin })),
            ]}
          />
        </Field>
        <Field htmlFor="emplacement-notes" label={t('regie:inventaire.note')}>
          <Textarea
            id="emplacement-notes"
            maxLength={5000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <label className={styles.case}>
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
          {t('regie:reglages.emplacements.actif')}
        </label>
        <Button
          disabled={
            !nom.trim() || !/^[A-Z0-9-]{1,16}$/.test(code) || !lieuId || enregistrer.isPending
          }
          onClick={() => enregistrer.mutate(undefined, { onSuccess: fermer })}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {enregistrer.error && (
          <p role="alert">
            {estApiError(enregistrer.error) ? enregistrer.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </div>
    </Dialog>
  );
}
export function EmplacementsPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const { id } = useParams();
  const lieux = useLieux(galerieSlug, true);
  const arbre = useEmplacements(galerieSlug, true);
  const detail = useQuery({
    queryKey: cleMetier(galerieSlug, 'reserve', 'emplacement', id),
    queryFn: (c) => reserveApi.emplacement(galerieSlug, id ?? '', appelDeRequete(c)),
    enabled: Boolean(id),
  });
  const tous = aplatirEmplacements(arbre.data?.emplacements ?? []);
  const [formulaire, setFormulaire] = useState<{ emplacement: EmplacementPresente | null } | null>(
    null,
  );
  const [desactivation, setDesactivation] = useState<EmplacementPresente | null>(null);
  const desactiver = useMutationReserve(galerieSlug, (emplacement: EmplacementPresente, appel) =>
    reserveApi.modifierEmplacement(galerieSlug, emplacement.id, { actif: false }, appel),
  );
  const gere = galerieALaction(galerie, 'inventaire:manage');
  function noeuds(liste: NoeudEmplacement[]) {
    return (
      <ul className={styles.arbre}>
        {liste.map((e) => (
          <li key={e.id}>
            <div className={styles.ligne}>
              <Link className={styles.lien} to={`/${galerieSlug}/reglages/emplacements/${e.id}`}>
                {e.code} · {e.nom} {e.actif ? '' : t('regie:reglages.emplacements.inactif')}
              </Link>
              {gere && (
                <div className={styles.actions}>
                  <Button variant="secondary" onClick={() => setFormulaire({ emplacement: e })}>
                    {t('regie:reglages.emplacements.modifier')}
                  </Button>
                  {e.actif && (
                    <Button variant="ghost" onClick={() => setDesactivation(e)}>
                      {t('regie:reglages.emplacements.desactiver')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {e.enfants.length > 0 && noeuds(e.enfants)}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <div>
          <h1>{t('regie:reglages.emplacements.titre')}</h1>
          <p>{t('regie:reglages.emplacements.sousTitre')}</p>
        </div>
        {gere && (
          <Button onClick={() => setFormulaire({ emplacement: null })}>
            {t('regie:reglages.emplacements.creer')}
          </Button>
        )}
      </header>
      {(arbre.isError || detail.isError) && <p role="alert">{t('commun:erreurReseau')}</p>}
      {arbre.isPending && <p role="status">{t('commun:chargement')}</p>}
      {detail.data && (
        <section className={styles.panneau}>
          <h2>{detail.data.emplacement.chemin}</h2>
          <p>
            {t('regie:reglages.emplacements.occupation', {
              oeuvres: detail.data.emplacement.nombreOeuvres,
              caisses: detail.data.emplacement.nombreCaisses,
            })}
          </p>
          <p>{detail.data.emplacement.notes}</p>
          <Etiquettes cibles={[{ type: 'emplacement', id: detail.data.emplacement.id }]} />
        </section>
      )}
      {lieux.data?.map((lieu) => (
        <section className={styles.panneau} key={lieu.id}>
          <h2>{lieu.nom}</h2>
          {noeuds((arbre.data?.emplacements ?? []).filter((e) => e.lieuId === lieu.id))}
        </section>
      ))}
      {!tous.length && !arbre.isPending && <p>{t('regie:reglages.emplacements.aucun')}</p>}
      {formulaire && (
        <FormulaireEmplacement
          emplacement={formulaire.emplacement}
          tous={tous}
          fermer={() => setFormulaire(null)}
        />
      )}
      <Dialog
        open={desactivation !== null}
        onOpenChange={(v) => {
          if (!v) setDesactivation(null);
        }}
        title={t('regie:reglages.emplacements.desactiver')}
        description={t('regie:reglages.emplacements.desactivationConfirmation')}
        closeLabel={t('commun:actions.fermer')}
      >
        <p>{desactivation?.chemin}</p>
        <Button
          disabled={desactiver.isPending}
          onClick={() => {
            if (desactivation)
              desactiver.mutate(desactivation, { onSuccess: () => setDesactivation(null) });
          }}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {desactiver.error && (
          <p role="alert">
            {estApiError(desactiver.error) ? desactiver.error.message : t('commun:erreurReseau')}
          </p>
        )}
      </Dialog>
    </section>
  );
}
